import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, Spinner } from 'reactstrap';

import { actionConfirmBooking } from 'store/booking/actions';
import { LOGIN_TYPE, storageKey } from 'constants/index';
import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import styles from './ModalConfirmBooking.module.scss';

function ModalConfirmBooking({
  isOpen,
  onClose,
  booking,
  onSuccess,
  isReadOnly: isReadOnlyProp,
}) {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.Booking.isLoadingConfirmBooking);
  const isIntranetRedux = useSelector((state) => state.Login?.isIntranet);
  const [confirmCode, setConfirmCode] = useState('');

  const isReadOnly = useMemo(() => {
    if (typeof isReadOnlyProp === 'boolean') return isReadOnlyProp;
    const loginType = localStorage.getItem(storageKey.LOGIN_TYPE);
    if (loginType === LOGIN_TYPE.EXTRANET) return false;
    if (loginType === LOGIN_TYPE.INTRANET) return true;
    return isIntranetRedux ?? false;
  }, [isReadOnlyProp, isIntranetRedux]);

  useEffect(() => {
    if (isOpen && booking) {
      setConfirmCode(booking.confirmBooking || '');
    }
  }, [isOpen, booking]);

  const handleSave = useCallback(() => {
    if (isReadOnly || !booking?._id) return;

    dispatch(
      actionConfirmBooking({
        id: booking._id,
        confirmBooking: confirmCode,
        callback: (res) => {
          onSuccess?.(booking._id, confirmCode, res);
          onClose();
        },
      }),
    );
  }, [booking, confirmCode, dispatch, isReadOnly, onClose, onSuccess]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isReadOnly) {
        handleSave();
      }
    }
  };

  if (!booking) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      fade
      className={styles.Modal}
    >
      <div className={styles.Header}>
        <h5 className={styles.Title}>Xác nhận</h5>
        <button
          type="button"
          className={styles.CloseButton}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseButtonSvg />
        </button>
      </div>

      <ModalBody className={styles.Body}>
        <div className={styles.UserInfo}>
          <div className={styles.FullName}>
            {booking.userInfo?.fullName || booking.userInfo?.displayName || 'Khách hàng'}
          </div>
          <div className={styles.OrderInfo}>
            <span>Mã đặt phòng:</span>
            <span className={styles.OrderId}>{booking.orderId}</span>
          </div>
        </div>

        <div className={styles.FieldGroup}>
          <label htmlFor="confirmBookingInput" className={styles.Label}>
            Mã xác nhận
          </label>
          <input
            id="confirmBookingInput"
            type="text"
            maxLength={100}
            className={styles.Input}
            placeholder={isReadOnly ? 'Chưa có mã xác nhận' : 'Nhập Mã xác nhận...'}
            value={confirmCode}
            onChange={(e) => {
              if (!isReadOnly) {
                setConfirmCode(e.target.value.slice(0, 100));
              }
            }}
            readOnly={isReadOnly}
            onKeyDown={handleKeyDown}
          />
        </div>
      </ModalBody>

      <div className={styles.Footer}>
        {isReadOnly ? (
          <button
            type="button"
            className={styles.CancelButton}
            onClick={onClose}
          >
            Đóng
          </button>
        ) : (
          <>
            <button
              type="button"
              className={styles.CancelButton}
              onClick={onClose}
            >
              Huỷ
            </button>
            <button
              type="button"
              className={styles.SaveButton}
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? <Spinner size="sm" color="light" /> : 'Lưu'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

ModalConfirmBooking.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  booking: PropTypes.instanceOf(Object),
  onSuccess: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

ModalConfirmBooking.defaultProps = {
  booking: null,
  onSuccess: undefined,
  isReadOnly: undefined,
};

export default React.memo(ModalConfirmBooking);
